<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import DecisionEngineNav from '@/V2/decision-engine/DecisionEngineNav.vue';
import DecisionEngineCardEdit from '@/V2/decision-engine/DecisionEngineCardEdit.vue';
import DecisionEngineRuleEditor from '@/V2/decision-engine/DecisionEngineRuleEditor.vue';
import RuleDelete from '@/V2/decision-engine/rules/RuleDelete.vue';
import RuleSave from '@/V2/decision-engine/rules/RuleSave.vue';
import { iButton, iIcon } from '@inscribe/signal';

export default {
  components: {
    DecisionEngineNav,
    DecisionEngineCardEdit,
    DecisionEngineRuleEditor,
    iButton,
    iIcon,
    RuleDelete,
    RuleSave,
  },
  props: {
    rule: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      rulePublicValue: null,
      newConditions: [],
      ruleNewName: '',
      ruleEnabled: null,
      ruleToBeDeleted: null,
      conditionsAreValid: false,
      rulesToBeSaved: null,
      shouldGoToRulesList: true,
    };
  },
  computed: {
    ...mapGetters(['getRules']),
    isRuleValid() {
      return this.hasConditions && this.nameIsValid && this.conditionsAreValid;
    },
    hasConditions() {
      return this.newConditions.length > 0;
    },
    isNew() {
      return this.rule.isDraft === true;
    },
    canBeEdited() {
      return !this.rule?.default;
    },
    nameIsValid() {
      const foundRules = this.getRules.filter(
        (r) => r.display_name === this.ruleNewName,
      );
      return this.ruleNewName?.trim() !== '' && foundRules.length < 2;
    },
  },
  mounted() {
    if (!this.rule) {
      this.goToRulesList();
    } else {
      this.rulePublicValue = this.rule.public;
      this.newConditions = [...(this.rule.conditions.all || [])];
      this.ruleEnabled = this.rule.enabled || false;
      this.ruleNewName = this.rule.display_name;
    }
  },
  methods: {
    ...mapActions(['createCheck', 'deleteCheck', 'saveCheck']),
    ...mapGetters(['getRuleIndex']),
    ...mapMutations({ updateCheck: 'updateCheck' }),
    goToRulesList() {
      this.$router.push({
        name: 'decision-engine-rules',
        params: { section: this.rule?.default ? 'default' : 'custom' },
      });
    },
    onRuleChange(value) {
      this.newConditions[value.index] = value.rule;
      this.setValidFlags();
    },
    onRuleDelete(value) {
      this.newConditions = [
        ...this.newConditions.slice(0, value.index),
        ...this.newConditions.slice(value.index + 1),
      ];
      this.setValidFlags();
    },
    saveOrCreateRule(goToRulesList = true) {
      if (!this.nameIsValid) {
        this.showMessage({
          title: 'Name not valid',
          text: 'Please make sure that the name is valid and not already in use',
        });
      } else {
        this.shouldGoToRulesList = goToRulesList;
        const rule = {
          actions: this.rule.actions,
          default: this.rule.default,
          conditions: {
            all: this.newConditions.map((c) => {
              delete c.isValid;
              delete c.valueLabels;
              return c;
            }),
          },
          enabled: this.ruleEnabled,
          name: this.rule.name,
          display_name: this.ruleNewName,
        };

        if (this.isNew) {
          this.rulesToBeSaved = [...this.getRules, rule];
        } else {
          const foundRuleIdx = this.getRuleIndex()(this.rule);
          if (foundRuleIdx > -1) {
            this.rulesToBeSaved = [
              ...this.getRules.slice(0, foundRuleIdx),
              rule,
              ...this.getRules.slice(foundRuleIdx + 1),
            ];
          }
        }
      }
    },
    setValidFlags() {
      let conditionsValue = true;
      this.newConditions.forEach((trigger) => {
        if (trigger.isValid === false) {
          conditionsValue = false;
        }
      });
      this.conditionsAreValid = conditionsValue;
    },
    onBackButtonClick() {
      this.goToRulesList();
    },
    onNameChange(value) {
      this.ruleNewName = value.trim();
    },
    onSwitchChange(value) {
      this.ruleEnabled = value;
      if (!this.isNew && this.isRuleValid) {
        this.saveOrCreateRule(false);
      }
    },
    onDelete() {
      if (this.isNew) {
        this.goToRulesList();
      } else {
        this.ruleToBeDeleted = this.rule;
      }
    },
    createCondition() {
      this.newConditions.push({
        name: null,
        operator: null,
        value: null,
      });
    },
    onRulesSaved() {
      if (this.shouldGoToRulesList) {
        this.goToRulesList();
      }
    },
    deleteRule(newRules) {
      this.rulesToBeSaved = newRules;
    },
    showMessage(options = {}) {
      this.$notify({
        group: 'api',
        type: options.isError ? 'error' : 'success',
        title: options.title,
        duration: options.duration || 5000,
        text: options.text || '',
      });
    },
  },
};
</script>

<template>
  <div v-if="rule" class="rule-edit edit-container d-flex flex-column">
    <decision-engine-nav
      class="edit-nav"
      category-name="Fraud Rules"
      :action-name="
        isNew
          ? 'New Rule'
          : canBeEdited
          ? 'Edit Rule'
          : rule.default
          ? 'Default Fraud Rule Detail'
          : 'Custom Fraud Rule Detail'
      "
      :button-label="isNew ? 'Create' : 'Update'"
      :button-disabled="!isRuleValid"
      :item-name="rule.display_name"
      :show-toggle="true"
      :toggle-value="ruleEnabled"
      :editable="canBeEdited"
      @on-back-button-click="onBackButtonClick"
      @on-name-change="onNameChange"
      @on-enabled-toggle-change="onSwitchChange"
      @on-delete="onDelete"
      @on-button-click="saveOrCreateRule"
    ></decision-engine-nav>

    <div class="content d-flex flex-column i-mt-10 i-gap-9">
      <!-- readonly warning -->
      <div
        v-if="rule.default"
        class="readonly-warning d-flex flex-row align-items-center i-gap-6 i-px-8 i-py-6 i-radius-2xl"
      >
        <i-icon
          symbol="small-solid-info"
          class="menu-icon i-text-neutral-100"
          :size="16"
        ></i-icon>
        <div class="i-text-neutral-120 i-text-subheading-2">
          Default fraud rules can only be enabled or disabled and cannot be
          modified
        </div>
      </div>

      <!-- rules filters -->
      <decision-engine-card-edit title="Fraudulent if">
        <div
          slot="content"
          :class="{ 'i-mt-9': hasConditions }"
          class="triggers-edit d-flex flex-column"
          data-testid="rule-requirements"
        >
          <decision-engine-rule-editor
            v-for="(condition, idx) of newConditions"
            :key="idx + '-' + condition.name"
            :rule="condition"
            :item-index="idx"
            :editable="canBeEdited"
            :force-value="idx === 0"
            @on-rule-change="(value) => onRuleChange(value)"
            @on-rule-delete="(value) => onRuleDelete(value)"
          >
          </decision-engine-rule-editor>
          <i-button
            v-if="hasConditions && canBeEdited"
            text="And"
            class="add-button"
            size="sm"
            type="secondary"
            data-testid="add-rule-condition"
            :disabled="!conditionsAreValid"
            @button-click="createCondition()"
          ></i-button>
        </div>
        <i-button
          v-if="!hasConditions && canBeEdited"
          slot="right-side"
          text="Add filter"
          class="add-first-button"
          size="xs"
          type="neutral"
          subtype="outline"
          @button-click="createCondition()"
        ></i-button>
      </decision-engine-card-edit>

      <!-- rule delete -->
      <rule-delete
        :rule-to-be-deleted="ruleToBeDeleted"
        @on-close="ruleToBeDeleted = null"
        @on-delete="deleteRule"
      ></rule-delete>

      <!-- rules save -->
      <rule-save
        :new-rules-collection="rulesToBeSaved"
        @on-saved="onRulesSaved"
      ></rule-save>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rule-edit {
  .decision-engine-rule-setter {
    &:not(:last-of-type) {
      &::after {
        content: 'And';
        color: $light-neutral-120;
        font-size: 12px;
        font-weight: 500;
        margin-top: 12px;
        margin-bottom: 12px;
      }
    }

    &:last-of-type {
      margin-bottom: 20px;
    }
  }

  .content {
    .readonly-warning {
      border: 1px solid $light-neutral-60;
    }

    .add-button {
      width: 60px;
    }
  }
}
</style>
